import { Handle, Position } from '@xyflow/react'
import { useState, useRef } from 'react'

function getBoneNames() {
  const scene = window.__AVATAR_SCENE__
  if (!scene) return []
  const names = new Set()
  scene.traverse(obj => {
    if (obj.isSkinnedMesh && obj.skeleton) {
      obj.skeleton.bones.forEach(b => { if (b.name) names.add(b.name) })
    }
  })
  return [...names].sort()
}

export function LayeredBoneBlendNode({ data, selected }) {
  const blendBone   = data.blendBone   ?? ''
  const blendWeight = data.blendWeight ?? 1.0
  const [boneNames, setBoneNames] = useState([])
  const [draft, setDraft] = useState(null) // null = not editing

  const handleFocus = () => {
    if (!boneNames.length) setBoneNames(getBoneNames())
    setDraft('') // clear so datalist shows all options
  }

  const handleChange = (e) => {
    setDraft(e.target.value)
    data.onChange?.('blendBone', e.target.value)
  }

  const handleBlur = () => setDraft(null)

  const listId = `bone-list-${data.id ?? 'lbb'}`

  return (
    <div style={{
      background: selected ? '#1a1f12' : '#111608',
      border: `2px solid ${selected ? '#90c030' : '#4a6018'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 185,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>🦴</span>
        <span style={{ color: '#a0d040', fontWeight: 'bold', fontSize: 10, letterSpacing: 0.5 }}>
          LAYERED BONE BLEND
        </span>
      </div>

      {/* Base input */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, position: 'relative' }}>
        <Handle
          type="target"
          position={Position.Left}
          id="base"
          style={{ background: '#e07040', width: 10, height: 10, top: '50%', transform: 'translateY(-50%)' }}
        />
        <span style={{ marginLeft: 14, color: '#aaa', fontSize: 11 }}>Base</span>
      </div>

      {/* Layer input */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, position: 'relative' }}>
        <Handle
          type="target"
          position={Position.Left}
          id="layer"
          style={{ background: '#e07040', width: 10, height: 10, top: '50%', transform: 'translateY(-50%)' }}
        />
        <span style={{ marginLeft: 14, color: '#aaa', fontSize: 11 }}>Layer</span>
      </div>

      {/* Blend Bone */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: '#607828', fontSize: 9, marginBottom: 3 }}>Blend Bone</div>
        <datalist id={listId}>
          {boneNames.map(n => <option key={n} value={n} />)}
        </datalist>
        <input
          type="text"
          list={listId}
          value={draft !== null ? draft : blendBone}
          onChange={handleChange}
          onMouseDown={e => e.stopPropagation()}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="e.g. spine_01"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#0a1200', border: `1px solid ${blendBone ? '#4a6018' : '#2a3010'}`,
            color: blendBone ? '#c0e060' : '#556030',
            padding: '3px 6px', borderRadius: 3,
            fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        />
      </div>

      {/* Weight slider + optional wire */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, position: 'relative' }}>
          <Handle
            type="target"
            position={Position.Left}
            id="weight-in"
            style={{ background: '#4ecf7e', width: 8, height: 8, top: '50%', transform: 'translateY(-50%)' }}
          />
          <span style={{ marginLeft: 12, color: '#4ecf7e', fontSize: 10 }}>Weight</span>
          <span style={{ color: '#aaa', fontSize: 10 }}>{blendWeight.toFixed(2)}</span>
        </div>
        <input
          type="range"
          min={0} max={1} step={0.01}
          value={blendWeight}
          onChange={e => data.onChange?.('blendWeight', parseFloat(e.target.value))}
          onMouseDown={e => e.stopPropagation()}
          style={{ width: '100%', accentColor: '#90c030', cursor: 'pointer' }}
        />
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="anim"
        style={{ background: '#e07040', width: 10, height: 10 }}
      />
    </div>
  )
}
